<template>
  <div
    v-if="log.issued_at"
    v-tooltip="manualRunTooltipText"
    class="-translate-y-0-25 transform cursor-pointer"
  >
    <icon-base :icon="IconManualRun" width="12" height="12" />
  </div>
  <div
    v-else
    v-tooltip="attribute?.service"
    class="flex min-w-fit cursor-pointer items-center space-x-1 rounded-sm px-1 py-0-25"
    :class="{
      'grayscale hover:grayscale-0': !log.issued_at,
      'bg-gray-100/80': deepEqual(newestAutomatedHistoryLog, log),
    }"
  >
    <span v-if="step">
      {{ step.title }}
    </span>

    <img
      class="h-5 w-5"
      :src="attributeImage"
      :alt="$t('ORCHESTRATION.SERVICE_IMAGE_ALT')"
      loading="lazy"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { format } from "date-fns";
import { deepEqual } from "fast-equals";
import { storeToRefs } from "pinia";

import { SERVICE_CUSTOM } from "@/helpers/constants";
import { LONG_LOCALIZED_DATE_TIME } from "@/helpers/constants/dateTime";
import { formatUserFullName } from "@/helpers/formatting";
import { getProviderImageByServiceName } from "@/helpers/services";
import { useAttributesOptionsMap } from "@/hooks/orchestration";
import { useUnderwritingLogs } from "@/hooks/underwriting";
import useApplicationsStore from "@/stores/applications";

import IconManualRun from "@/components/icons/IconManualRun.vue";

import type { IDataOrchestrationLogEntry } from "@/models/applications";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  log: {
    type: Object as PropType<IDataOrchestrationLogEntry>,
    required: true,
  },
});

const { allAttributes, customAttributes } = useAttributesOptionsMap();
const { getters } = useStore();
const { newestAutomatedHistoryLog } = useUnderwritingLogs();
const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const masterStatuses = computed<
  Record<DataOrchestrationMasterStatusType, DataOrchestrationMasterStatus>
>(() => getters["options/dataOrchestrationMasterStatuses"]);

const allTemplateSteps = computed(
  () => activeDataOrchestrationLog.value?.template?.content?.steps || []
);

const step = computed(() =>
  allTemplateSteps.value.find((step) => step.id === props.log?.step_id)
);

const attribute = computed(() => {
  // Taking the first [0] attribute because only one
  // service provider can be selected at the time on the step
  const id = step.value?.attributes?.[0]?.id;
  if (!id) {
    return undefined;
  }
  if (id === SERVICE_CUSTOM) {
    return customAttributes.value[0];
  }
  return allAttributes.value[id];
});

const attributeImage = computed(() => {
  return getProviderImageByServiceName(attribute.value?.service || "") || "";
});

const manualRunTooltipText = computed(() => {
  const { log } = props;
  if (!log.context?.master_status || !log.user || !log.issued_at) {
    return "";
  }
  const masterStatus = masterStatuses.value?.[log.context.master_status] || "";
  const fullUserName = formatUserFullName(log.user);
  const dateTime = format(new Date(log.issued_at), LONG_LOCALIZED_DATE_TIME);
  return `${masterStatus}\nby ${fullUserName}\n${dateTime}`;
});
</script>
